<template>
  <div
    class="background-media critical-component-clear"
    :class="{
      'background-media--windowed':
        componentSettings?.layout === 'index'
        && (isMobile || (!isMobile && contentPlacement !== 'full')),
      'background-media--half': contentPlacement === 'half',
      'background-media--full-bleed-video': fullBleedVideo,
      'background-media--default-height--desktop': desktopMediaSettings.desktopDefaultHeight,
      'background-media--default-height--mobile': mobileMediaSettings.mobileDefaultHeight,
    }"
    :style="`
      --mobile-aspect-ratio: ${mobileAspectRatio};
      --desktop-aspect-ratio: ${desktopAspectRatio};
    `"
  >
    <!--
      We window the column content so that it is always vertically centered
      to the viewport height.
    -->

    <div
      v-if="
        isMobile &&
          componentSettings?.buttonText &&
          componentSettings?.buttonURL &&
          componentSettings?.buttonSticky"
      class="
      background-media__bottom-cta"
      :class="[
        `section-content--${componentSettings?.contentColor}`
      ]"
    >
      <btn
        :label="componentSettings.buttonText"
        :url="componentSettings.buttonURL"
        modifiers="text"
      />
    </div>

    <!-- Full bleed image if media content is not windowed. -->
    <art-direction
      v-if="mobileMediaSettings?.mobileImage?.src"
      :block-class="blockClass"
      :caption="fullImageCaption"
      caption-align="center"
      :image="mobileMediaSettings?.mobileImage"
      :image-desktop="desktopMediaSettings?.desktopImage || mobileMediaSettings?.mobileImage"
      :padding="parent === 'page-media'"
      :ratio="mobileRatio"
      :ratio-desktop="desktopRatio"
      @art-direction-loaded="$emit('carousel-image-loaded', true)"
    />

    <component
      :is="element"
      v-if="(checkIfShopifyVideo() || checkIfVimeoVideo())"
      class="background-media__video-container"
      :href="sectionUrl"
    >
      <video
        v-if="checkIfShopifyVideo()"
        ref="browserPlayer"
        :poster="posterImage?.src"
        class="background-media__video-player"
        :autoplay="componentSettings.autoplay"
        :muted="componentSettings.muted"
        :loop="componentSettings.loop"
        playsinline
        @play="videoPlaying = true"
        @pause="videoPlaying = false"
      >
        <source :src="videoSource?.url">
      </video>

      <div
        v-if="checkIfVimeoVideo()"
        class="background-media__video-player"
      >
        <div
          ref="vimeoPoster"
          class="background-media__vimeoPoster"
        >
          <responsive-image
            v-if="posterImage"
            block-class="background-media"
            :image="posterImage"
            :padding="false"
            :ratio="false"
          />
        </div>

        <div
          ref="vimeoPlayer"
          :vimeoVideoMuted="vimeoVideoMuted"
        />
      </div>
    </component>

    <div
      v-if="componentSettings.hasControls
        && (checkIfShopifyVideo() || checkIfVimeoVideo())"
      class="background-media__controls"
      :js-sticky-child="!isMobile && contentPlacement === 'half'"
    >
      <button
        ref="statusButton"
        type="button"
        class="background-media__status-button"
      >
        <div
          ref="statusButtonPlay"
          class="background-media__status-play"
          :class="{
            [$class('hidden')]: videoPlaying,
          }"
        >
          <icon-play />
          <span
            class="
              text-desktop-regular-uc-desktop
              text-mobile-regular-uc-mobile
            "
            v-text="$string('accessibility.play')"
          />
        </div>
        <div
          ref="statusButtonPause"
          class="background-media__status-pause"
          :class="{
            [$class('hidden')]: !videoPlaying,
          }"
        >
          <icon-pause />
          <span
            class="
              text-desktop-regular-uc-desktop
              text-mobile-regular-uc-mobile
            "
            v-text="$string('accessibility.pause')"
          />
        </div>
      </button>
      <div class="background-media__progress-bar-outer">
        <span
          ref="progressBarInner"
          class="background-media__progress-bar-inner"
        />
      </div>
      <button
        ref="audioButton"
        type="button"
        class="background-media__audio-button"
      >
        <span
          ref="audioButtonMute"
          class="
            background-media__audio-mute
            text-desktop-regular-uc-desktop
            text-mobile-regular-uc-mobile
          "
          :class="{
            [$class('hidden')]: componentSettings.muted,
          }"
          v-text="$string('accessibility.mute')"
        />
        <span
          ref="audioButtonUnMute"
          class="
            background-media__audio-unmute
            text-desktop-regular-uc-desktop
            text-mobile-regular-uc-mobile
          "
          :class="{
            [$class('hidden')]: !componentSettings.muted,
          }"
          v-text="$string('accessibility.unmute')"
        />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Background media (background-media)
 * -----------------------------------------------------------------------------
 * Snippet for background media.
 *
 * @param {Number} carouselIndex - Index of the current slide in the carousel.
 * @param {Number} elementIndex - Index the element is within its parent array.
 * @param {String} contentPlacement - String indicating if the component is
 * occupying half or full width.
 * @param {Boolean} fullBleedVideo - Is the media inside a full-bleed-video.
 * @param {String} parent - Name of the parent component.
 */

import Btn from '~global/btn/btn'
import Player from '@vimeo/player'
import IconPlay from '~icons/direction/chevron/right.svg'
import IconPause from '~icons/misc/pause.svg'
import { mapState } from 'vuex'
import ResponsiveImage from '~global/images/responsive-image'
import ArtDirection from '~global/images/art-direction'

export default {
  name: 'BackgroundMedia',

  components: {
    ArtDirection,
    Btn,
    IconPlay,
    IconPause,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },

    carouselIndex: {
      type: Number,
      default: 0,
    },

    contentPlacement: {
      type: String,
      default: 'full',
    },

    elementIndex: {
      type: Number,
      default: 0,
    },

    fullBleedVideo: {
      type: Boolean,
      default: false,
    },

    componentSettings: {
      type: Object,
      default: () => ({
        titleText: '',
        titleMobileSize: '',
        titleDesktopSize: '',
        bodyText: '',
        bodyMobileSize: '',
        bodyDesktopSize: '',
        buttonText: '',
        buttonURL: '',
        imageCaption: '',
        layout: '',
        hasControls: false,
        hasContent: false,
        hasIndexLayout: true,
        hasStickyContent: false,
        autoplay: true,
        loop: true,
        muted: true,
      }),
    },

    mobileMediaSettings: {
      type: Object,
      default: () => ({
        mobileDefaultHeight: '',
        mobileVideoFile: '',
        mobileVimeoId: '',
        mobileImage: '',
      }),
    },

    desktopMediaSettings: {
      type: Object,
      default: () => ({
        desktopDefaultHeight: '',
        desktopVideoFile: '',
        desktopVimeoId: '',
        desktopImage: '',
      }),
    },

    parent: {
      type: String,
      default: '',
    },
  },

  emits: {
    'carousel-image-loaded': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      findParent: false,
      player: null,
      posterImage: null,
      videoPlaying: false,
      vimeoVideoMuted: this.componentSettings.muted,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Computes image caption
     */
    fullImageCaption() {
      if (this.componentSettings?.layout === 'index' || this.parent === 'page-media') {
        return this.componentSettings.imageCaption
      }

      return ''
    },

    /**
     * Computes and returns a mobile image aspect ratio based on image size.
     */
    mobileAspectRatio() {
      const image = this.mobileMediaSettings.mobileImage

      if (!image.aspectRatio && (!image.width || !image.height)) {
        return 'unset'
      }

      if (image.aspectRatio || image.aspect_ratio) {
        return Number((image.aspectRatio || image.aspect_ratio).toFixed(2)) + 0.01
      }

      return Number((image.width / image.height).toFixed(2)) + 0.01
    },

    /**
     * Computes and returns a mobile image aspect ratio based on section setup.
     */
    mobileRatio() {
      if (this.mobileMediaSettings.mobileDefaultHeight || this.parent === 'page-media') {
        return 'image'
      }

      return 0.72
    },

    /**
     * Computes and returns a desktop image aspect ratio based on image size.
     */
    desktopAspectRatio() {
      const image = this.desktopMediaSettings.desktopImage

      if (!image.aspectRatio && (!image.width || !image.height)) {
        return 'unset'
      }

      if (image.aspectRatio || image.aspect_ratio) {
        return image.aspectRatio || image.aspect_ratio
      }

      return image.width / image.height
    },

    /**
     * Computes and returns a desktop image aspect ratio based on section setup.
     */
    desktopRatio() {
      if (this.desktopMediaSettings.desktopDefaultHeight || this.parent === 'page-media') {
        return 'image'
      }

      return this.contentPlacement === 'half' ? 0.72 : 1.6
    },

    /**
     * Shows only the active slide CTA.
     */
    isActiveAction() {
      if (this.componentSettings.type !== 'carousel') {
        return true
      }

      return this.carouselIndex === this.elementIndex
    },

    /**
     * Returns the best video src from the supplied video (based on breakpoint).
     */
    videoSource() {
      return this.isMobile
        ? this.getMp4Source(this.mobileMediaSettings.mobileVideoFile)?.[0]
        : this.getMp4Source(this.desktopMediaSettings.desktopVideoFile)?.[0]
    },

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      return this.componentSettings.buttonURL ? 'a' : 'div'
    },

    /**
     * Compute element url.
     * @returns {String}
     */
    sectionUrl() {
      return this.componentSettings.buttonURL ? this.componentSettings.buttonURL : null
    },
  },

  watch: {
    videoPlaying() {
      if (this.checkIfShopifyVideo()) {
        this.$refs.browserPlayer.addEventListener('ended', () => {
          this.toggleStatusButton()
        })
      }
    },
  },

  mounted() {
    this.getPoster()

    if (this.checkIfVimeoVideo()) {
      this.initVimeoPlayer()
    }

    if (this.componentSettings.hasControls &&
      (this.$refs.browserPlayer || this.$refs.vimeoPlayer)) {
      this.setEventListeners()
    }

    this.$nextTick().then(() => {
      this.findParent = true
    })
  },

  methods: {

    /**
     * Returns the highest resolution mp4 video source from the supplied video.
     * @param {Object} video - Shopify video object.
     * @returns {Array}
     */
    getMp4Source(video) {
      const sources = [...video.sources.filter(source => source.format === 'mp4')]
      return sources.sort((a, b) => b.width - a.width)
    },

    /**
     * Initalize Vimeo Player
     */
    initVimeoPlayer() {
      const options = {
        autoplay: this.componentSettings.autoplay,
        loop: this.componentSettings.loop,
        muted: this.componentSettings.muted,
        controls: false,
        title: false,
        transparent: false,
        portrait: false,
        byline: false,
        dnt: true,
        id: this.isMobile
          ? this.mobileMediaSettings.mobileVimeoId
          : this.desktopMediaSettings.desktopVimeoId,
      }
      this.vimeoPlayer = new Player(this.$refs.vimeoPlayer, options)
    },

    /**
     * Gets the node to teleport the element to.
     * This is required only if the element is nested inside ANY parent that
     * has transform set as a CSS property, which prevents the fixed positioning
     * taking effect.
     */
    getTeleportNode() {
      const parent = this.$el?.closest('[js-sticky-parent]')
      return parent
    },

    /**
     * Set Event Listeners
     */
    setEventListeners() {

      if (this.checkIfVimeoVideo()) {
        this.$refs.statusButton.addEventListener('click', () => this.videoControls('status', 'vimeo'))
        this.$refs.audioButton.addEventListener('click', () => this.videoControls('audio', 'vimeo'))

        this.vimeoPlayer.on('timeupdate', () => this.videoProgress('vimeo'))

        if (this.componentSettings.autoplay) {
          this.videoPlaying = true
          this.vimeoPlayer.on('loaded', this.removePoster)
        }

        this.vimeoPlayer.on('ended', () => {
          this.videoPlaying = false
          this.toggleStatusButton()
        })

      } else if (this.checkIfShopifyVideo()) {
        this.$refs.statusButton.addEventListener('click', () => this.videoControls('status', 'browser'))
        this.$refs.audioButton.addEventListener('click', () => this.videoControls('audio', 'browser'))

        this.$refs.browserPlayer.addEventListener('timeupdate', () => this.videoProgress('browser'))
      }
    },

    /**
     * Control video events based on type of video player
     */
    videoControls(event, playerType) {
      if (playerType === 'browser') {
        if (event === 'status') {
          if (this.$refs.browserPlayer.paused === false &&
            this.$refs.browserPlayer.ended === false) {
            this.$refs.browserPlayer.pause()
          } else {
            this.$refs.browserPlayer.play()
          }
        } else if (event === 'audio') {
          if (this.$refs.browserPlayer.muted) {
            this.$refs.browserPlayer.muted = false
          } else {
            this.$refs.browserPlayer.muted = true
          }
          this.toggleAudioButton()
        }
      } else if (playerType === 'vimeo') {
        if (event === 'status') {
          this.vimeoPlayer.getPaused().then((paused) => {
            if (paused) {
              this.videoPlaying = true
              this.vimeoPlayer.play()

              if (!this.componentSettings.autoplay) {
                this.removePoster()
              }
            } else {
              this.videoPlaying = false
              this.vimeoPlayer.pause()
            }
          })
        } else if (event === 'audio') {
          this.toggleAudioButton()

          if (this.vimeoVideoMuted) {
            this.vimeoPlayer.setMuted(true)

          } else {
            this.vimeoPlayer.setMuted(false)
          }
        }
      }
    },

    /**
     * Update video progress bar based on current time
     */
    videoProgress(playerType) {
      if (playerType === 'browser') {
        const currentTime = this.$refs.browserPlayer.currentTime
        const duration = this.$refs.browserPlayer.duration

        const progress = (100 * currentTime) / duration

        this.$refs.progressBarInner.style.width = `${progress}%`
      } else if (playerType === 'vimeo') {
        this.vimeoPlayer.getCurrentTime().then((currentTime) => {
          this.vimeoPlayer.getDuration().then((duration) => {
            const progress = (100 * currentTime) / duration
            this.$refs.progressBarInner.style.width = `${progress}%`
          })
        })
      }
    },

    /**
     * Get poster image source
     */
    getPoster() {
      this.posterImage = this.isMobile
        ? this.mobileMediaSettings?.mobileImage
        : this.desktopMediaSettings?.desktopImage
    },

    /**
     * Remove poster in vimeo
     */
    removePoster() {
      this.$refs.vimeoPoster.classList.add(this.$class('hidden'))
    },

    /**
     * Toggle Player status buttons
     */
    toggleStatusButton() {
      this.$refs.statusButtonPause.classList.toggle(this.$class('hidden'))
      this.$refs.statusButtonPlay.classList.toggle(this.$class('hidden'))
    },

    /**
     * Toggle Player mute/unmute buttons
     */
    toggleAudioButton() {
      this.vimeoVideoMuted = !this.vimeoVideoMuted
      this.$refs.audioButtonMute.classList.toggle(this.$class('hidden'))
      this.$refs.audioButtonUnMute.classList.toggle(this.$class('hidden'))
    },

    /**
     * Check if shopify video is used
     */
    checkIfShopifyVideo() {
      return (this.mobileMediaSettings.mobileVideoFile && this.isMobile) ||
        (this.desktopMediaSettings.desktopVideoFile && !this.isMobile)
    },

    /**
     * Check if vimeo video is used
     */
    checkIfVimeoVideo() {
      return ((this.mobileMediaSettings.mobileVimeoId && this.isMobile) ||
        (this.desktopMediaSettings.desktopVimeoId && !this.isMobile)) &&
        ((!this.mobileMediaSettings.mobileVideoFile && this.isMobile) ||
          (!this.desktopMediaSettings.desktopVideoFile && !this.isMobile))
    },
  },
}
</script>

<style lang="scss">
@import './background-media';
</style>
